import { useRef, useState } from "react";
import { api, ApiError, errMsg, makeIdem, type BankStatementDetail, type PasteBatchResult, type PasteMapping, type PastePreview, type PasteRow } from "../../api";
import { Modal } from "../../components/Modal";
import { Btn, Field, Notice, Select } from "../../components/ui";
import { fmtDay, formatMoney } from "../../lib/money";

const idemHdr = (i: ReturnType<typeof makeIdem>) => ({ "idempotency-key": i.get()["idempotency-key"] });
/** Teto de leitura no navegador (o limite de 200 KB é do servidor, que responde com a mensagem exata); evita ler arquivos absurdos para a memória. */
const READ_CEILING = 1_000_000;

type Opts = {
  separator: "auto" | ";" | "," | "tab"; numberFormat: "pt-BR" | "internacional"; header: "auto" | "sim" | "nao"; mapping: PasteMapping;
  source: "colagem" | "arquivo"; fileName?: string; fileSize?: number; encoding?: "UTF-8" | "UTF-8-BOM" | "WINDOWS-1252";
};
const SEP_LABEL: Record<string, string> = { ";": "ponto e vírgula", ",": "vírgula", tab: "tabulação" };

/** UTF-8 (com ou sem BOM); se não for UTF-8 válido, Windows-1252 (comum em exportações de bancos). O texto é mostrado para conferir os acentos. */
async function readCsv(file: File): Promise<{ text: string; encoding: "UTF-8" | "UTF-8-BOM" | "WINDOWS-1252" }> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  const bom = bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf;
  try { return { text: new TextDecoder("utf-8", { fatal: true }).decode(bytes.subarray(bom ? 3 : 0)), encoding: bom ? "UTF-8-BOM" : "UTF-8" }; }
  catch { return { text: new TextDecoder("windows-1252").decode(bytes), encoding: "WINDOWS-1252" }; }
}

/**
 * DEC-066/080/112 — assistente de colagem e CSV. Passo 1: colar ou escolher o arquivo; passo 2: prévia interpretada PELO SERVIDOR (erros por linha,
 * duplicidades, totais); passo 3: confirmar. A tela não interpreta datas nem valores: só envia o texto e mostra o que o servidor devolve.
 * Nada é gravado antes da confirmação e o sucesso só é mostrado depois da resposta do servidor.
 */
export function PasteWizard({ realm, d, onClose, onDone }: { realm: string; d: BankStatementDetail; onClose: () => void; onDone: (text: string) => void }) {
  const [text, setText] = useState("");
  const [opts, setOpts] = useState<Opts>({ separator: "auto", numberFormat: "pt-BR", header: "auto", mapping: {}, source: "colagem" });
  const [exclude, setExclude] = useState<number[]>([]);
  const [confirmed, setConfirmed] = useState<number[]>([]);
  const [pv, setPv] = useState<PastePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<PasteBatchResult | null>(null);
  const idem = useRef(makeIdem());
  const seq = useRef(0);
  const money = (m: { decimal: string }) => formatMoney(m.decimal, d.currency);

  const body = (o: Opts, ex: number[]) => ({
    text, source: o.source, ...(o.source === "arquivo" ? { fileName: o.fileName, fileSize: o.fileSize, encoding: o.encoding } : {}),
    separator: o.separator, numberFormat: o.numberFormat, ...(o.header !== "auto" ? { hasHeader: o.header === "sim" } : {}),
    ...(Object.keys(o.mapping).length ? { mapping: o.mapping } : {}), ...(ex.length ? { excludeRows: ex } : {}),
  });
  const stale = () => { setPv(null); setConfirmed([]); setResult(null); };
  const change = (patch: Partial<Opts>) => { setOpts((o) => ({ ...o, ...patch })); stale(); };

  async function pick(f: File | undefined) {
    if (!f) return;
    setErr(null);
    if (!/\.csv$/i.test(f.name)) return setErr("Só é aceito arquivo com extensão .csv.");
    if (f.size > READ_CEILING) return setErr("O arquivo é grande demais. O limite do assistente é de 200 KB por operação.");
    try {
      const r = await readCsv(f);
      setText(r.text); setExclude([]); stale();
      setOpts((o) => ({ ...o, source: "arquivo", fileName: f.name, fileSize: f.size, encoding: r.encoding, mapping: {} }));
    } catch { setErr("Não foi possível ler o arquivo."); }
  }

  async function runPreview(o: Opts = opts, ex: number[] = exclude) {
    const my = ++seq.current;
    setBusy(true); setErr(null);
    try {
      const r = await api<PastePreview>("POST", `/api/${realm}/bank-statements/${d.id}/lines/preview`, body(o, ex));
      if (my !== seq.current) return;
      setPv(r); setConfirmed((c) => c.filter((n) => r.rows.some((x) => x.row === n && x.status === "duplicada")));
    } catch (x) { if (my === seq.current) { setPv(null); setErr(errMsg(x)); } } finally { if (my === seq.current) setBusy(false); }
  }

  async function save() {
    if (!pv) return;
    setBusy(true); setErr(null);
    try {
      const r = await api<PasteBatchResult>("POST", `/api/${realm}/bank-statements/${d.id}/lines/batch`, {
        ...body(opts, exclude), version: pv.statement.version, contentHash: pv.contentHash, ...(confirmed.length ? { confirmDuplicateRows: confirmed } : {}),
      }, idemHdr(idem.current));
      idem.current.renew();
      setResult(r); setPv(null);
    } catch (x) {
      if (x instanceof ApiError) idem.current.renew();
      if (x instanceof ApiError && ["preview_stale", "version_conflict", "statement_not_open"].includes(x.code)) { setPv(null); }
      setErr(errMsg(x));
    } finally { setBusy(false); }
  }

  const dupRows = pv?.rows.filter((r) => r.status === "duplicada") ?? [];
  const pendingDup = dupRows.filter((r) => !confirmed.includes(r.row));
  const cols = pv ? Array.from({ length: pv.columnCount }, (_, i) => ({ i, label: pv.headers?.[i] ? `${i + 1} · ${pv.headers[i]}` : `Coluna ${i + 1}` })) : [];
  const mapSel = (key: keyof PasteMapping, label: string, optional = false) => (
    <Field label={label}>
      <Select value={String((pv?.mapping[key] ?? "") as number | string)} name={`map-${key}`} onChange={(e) => change({ mapping: { ...opts.mapping, ...(pv?.mapping ?? {}), [key]: e.target.value === "" ? null : Number(e.target.value) } })}>
        <option value="">{optional ? "— nenhuma —" : "Selecione…"}</option>
        {cols.map((c) => <option key={c.i} value={c.i}>{c.label}</option>)}
      </Select>
    </Field>
  );

  const toggleDup = (n: number) => setConfirmed((c) => (c.includes(n) ? c.filter((x) => x !== n) : [...c, n]));
  const removeRow = (n: number) => { const ex = [...exclude, n]; setExclude(ex); setPv(null); void runPreview(opts, ex); };
  const restoreRows = () => { setExclude([]); setPv(null); void runPreview(opts, []); };

  // ------------------------------------------------------------ resultado (depois da confirmação do servidor)
  if (result) {
    return (
      <Modal title="Linhas gravadas" onClose={() => onDone(`${result.lines} ${result.lines === 1 ? "linha gravada" : "linhas gravadas"} no extrato.`)}>
        <Notice kind="ok"><span data-paste-result>{result.lines} {result.lines === 1 ? "linha gravada" : "linhas gravadas"} como pendentes{result.duplicatesConfirmed ? `, sendo ${result.duplicatesConfirmed} duplicidade(s) confirmada(s)` : ""}.</span></Notice>
        <p className="small">Entradas: <strong className="tabular pos">{money(result.totalIn)}</strong> · Saídas: <strong className="tabular neg">{money(result.totalOut)}</strong> · Líquido: <strong className="tabular">{money(result.net)}</strong></p>
        <p className="muted small">Agora concilie cada linha com as movimentações do MM MONEY. Nada foi conciliado automaticamente e nenhum saldo foi alterado.</p>
        <div className="modal-actions"><Btn onClick={() => onDone(`${result.lines} ${result.lines === 1 ? "linha gravada" : "linhas gravadas"} no extrato.`)} data-paste-finish>Concluir</Btn></div>
      </Modal>
    );
  }

  return (
    <Modal title={`Colar dados ou importar CSV — ${fmtDay(d.from)} a ${fmtDay(d.to)}`} onClose={onClose} wide>
      <div data-paste-wizard>
        {err && <Notice kind="error">{err}</Notice>}
        {!pv && (
          <>
            <p className="muted small" style={{ marginTop: 0 }}>Cole o texto copiado de uma planilha ou do internet banking, ou escolha um arquivo .csv. Nada é gravado antes da sua confirmação, depois de conferir a prévia.</p>
            <label className="field"><span>Dados do extrato</span>
              <textarea className="input" rows={8} value={text} onChange={(e) => { setText(e.target.value); setExclude([]); stale(); setOpts((o) => ({ ...o, source: "colagem", fileName: undefined, fileSize: undefined, encoding: undefined })); }} name="pasteText" placeholder={"Data;Descrição;Valor;Documento\n05/03/2026;PIX RECEBIDO;1.200,00;\n06/03/2026;COMPRA MERCADO;-45,90;"} style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }} spellCheck={false} />
            </label>
            <div className="row" style={{ alignItems: "end" }}>
              <div className="grow"><Field label="…ou escolha um arquivo .csv"><input className="input" type="file" accept=".csv,text/csv" name="pasteFile" data-paste-file onChange={(e) => void pick(e.target.files?.[0])} /></Field></div>
              {opts.source === "arquivo" && <p className="small muted grow" data-paste-file-info>Arquivo <strong>{opts.fileName}</strong> · codificação {opts.encoding}. Confira os acentos na prévia.</p>}
            </div>
            <div className="grid2">
              <Field label="Formato dos números (escolha o do seu banco)">
                <Select value={opts.numberFormat} onChange={(e) => change({ numberFormat: e.target.value as Opts["numberFormat"] })} name="pasteFormat">
                  <option value="pt-BR">Brasileiro — 1.234,56</option><option value="internacional">Internacional — 1,234.56</option>
                </Select>
              </Field>
              <Field label="Separador de colunas">
                <Select value={opts.separator} onChange={(e) => change({ separator: e.target.value as Opts["separator"] })} name="pasteSeparator">
                  <option value="auto">Detectar automaticamente</option><option value=";">Ponto e vírgula (;)</option><option value=",">Vírgula (,)</option><option value="tab">Tabulação (planilha)</option>
                </Select>
              </Field>
            </div>
            <Field label="A primeira linha é cabeçalho?">
              <Select value={opts.header} onChange={(e) => change({ header: e.target.value as Opts["header"] })} name="pasteHeader">
                <option value="auto">Detectar automaticamente</option><option value="sim">Sim</option><option value="nao">Não — todas as linhas são dados</option>
              </Select>
            </Field>
            <div className="modal-actions">
              <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
              <Btn disabled={busy || !text.trim()} onClick={() => void runPreview()} data-paste-preview>{busy ? "Interpretando…" : "Pré-visualizar"}</Btn>
            </div>
          </>
        )}

        {pv && (
          <>
            <div className="summary" data-paste-summary>
              <div className="sum-card"><span className="muted">Linhas</span>
                <div className="small">Total: <strong data-paste-lines>{pv.totals.lines}</strong></div>
                <div className="small">Prontas: <strong data-paste-ok>{pv.totals.ok}</strong> · Com erro: <strong data-paste-errors>{pv.totals.errors}</strong></div>
                <div className="small">Duplicadas: <strong data-paste-dups>{pv.totals.duplicates}</strong> · Removidas: <strong>{pv.totals.excluded}</strong></div></div>
              <div className="sum-card"><span className="muted">Valores lidos</span>
                <div className="small">Entradas: <strong className="tabular pos" data-paste-in>{money(pv.totals.in)}</strong></div>
                <div className="small">Saídas: <strong className="tabular neg" data-paste-out>{money(pv.totals.out)}</strong></div>
                <div className="small">Líquido: <strong className="tabular">{money(pv.totals.net)}</strong></div></div>
              <div className="sum-card"><span className="muted">Frente ao saldo informado</span>
                <div className="small">Movimento esperado: <strong className="tabular">{money(pv.comparison.expectedNet)}</strong></div>
                <div className="small">Já nas linhas: <strong className="tabular">{money(pv.comparison.existingNet)}</strong></div>
                <div className="small">Faltaria: <strong className="tabular" data-paste-remaining>{money(pv.comparison.remaining)}</strong></div></div>
              <div className="sum-card"><span className="muted">Leitura</span>
                <div className="small">Separador: {SEP_LABEL[pv.separator]}</div>
                <div className="small">Números: {pv.numberFormat === "pt-BR" ? "brasileiro" : "internacional"}{pv.encoding ? ` · ${pv.encoding}` : ""}</div>
                <div className="small">{(pv.bytes / 1024).toFixed(1).replace(".", ",")} KB de {Math.floor(pv.limits.maxBytes / 1024)} KB · limite de {pv.limits.maxLines} linhas</div></div>
            </div>
            {pv.repeatedPaste.suspected && <Notice><span data-paste-repeated>Esta colagem parece já ter sido carregada: {pv.repeatedPaste.sameContentLoaded ? "o mesmo conteúdo já foi gravado nesta conta" : `${pv.repeatedPaste.matchingRows} de ${pv.repeatedPaste.validRows} linhas coincidem com linhas já gravadas`}. Confira antes de confirmar as duplicidades.</span></Notice>}
            {pv.blockers.includes("mapping_required") && <Notice kind="error"><span data-paste-mapping-needed>Não foi possível identificar as colunas ({pv.mappingMissing.join(", ")}). Indique abaixo qual coluna é cada campo e gere a prévia de novo.</span></Notice>}
            {pv.blockers.includes("rows_with_errors") && <Notice kind="error">Há linhas com erro. Corrija o texto e gere a prévia de novo, ou remova as linhas com erro do lote.</Notice>}
            {pv.blockers.includes("no_lines") && <Notice kind="error">Não há linhas para gravar.</Notice>}

            <details open={pv.blockers.includes("mapping_required")} style={{ marginBottom: 12 }}>
              <summary className="small">Colunas lidas</summary>
              <div className="grid2" style={{ marginTop: 8 }}>
                {mapSel("date", "Data")}{mapSel("description", "Descrição")}
                {mapSel("amount", "Valor (coluna única)", true)}{mapSel("document", "Documento (opcional)", true)}
                {mapSel("debit", "Débito (se separado)", true)}{mapSel("credit", "Crédito (se separado)", true)}
                {mapSel("indicator", "Indicador D/C (opcional)", true)}
              </div>
              <p className="small muted">Ao alterar uma coluna, gere a prévia de novo.</p>
              <Btn variant="ghost" onClick={() => void runPreview()} data-paste-repreview>Gerar prévia de novo</Btn>
            </details>

            {pv.rows.length > 0 && (
              <div className="card table-card" style={{ maxHeight: 340, overflow: "auto" }}>
                <table aria-label="Prévia das linhas" data-paste-rows>
                  <thead><tr><th>#</th><th>Data</th><th>Descrição</th><th className="num">Valor</th><th>Situação</th><th aria-label="Ações" /></tr></thead>
                  <tbody>
                    {pv.rows.map((r) => <PreviewRow key={r.row} r={r} d={d} confirmed={confirmed.includes(r.row)} onToggle={() => toggleDup(r.row)} onRemove={() => removeRow(r.row)} />)}
                  </tbody>
                </table>
              </div>
            )}
            {exclude.length > 0 && <p className="small muted">{exclude.length} linha(s) removida(s) do lote. <button className="link" onClick={restoreRows} data-paste-restore>Restaurar todas</button></p>}

            <div className="modal-actions">
              <Btn variant="ghost" onClick={() => { setPv(null); }} data-paste-back>← Editar os dados</Btn>
              <button type="button" className="btn primary" disabled={busy || !pv.canSave || pendingDup.length > 0} onClick={() => void save()} data-confirm-paste>
                {busy ? "Gravando…" : `Gravar ${pv.totals.ok + pv.totals.duplicates} ${pv.totals.ok + pv.totals.duplicates === 1 ? "linha" : "linhas"}`}
              </button>
            </div>
            {pendingDup.length > 0 && pv.canSave && <p className="small muted" style={{ textAlign: "right" }} data-paste-dup-hint>Confirme ou remova cada linha duplicada ({pendingDup.length} pendente{pendingDup.length > 1 ? "s" : ""}) para gravar.</p>}
          </>
        )}
      </div>
    </Modal>
  );
}

function PreviewRow({ r, d, confirmed, onToggle, onRemove }: { r: PasteRow; d: BankStatementDetail; confirmed: boolean; onToggle: () => void; onRemove: () => void }) {
  const label = { ok: "Pronta", erro: "Erro", duplicada: "Duplicada", excluida: "Removida" }[r.status];
  const cls = r.status === "erro" ? "pill late" : r.status === "ok" ? "pill accent" : "pill";
  return (
    <tr data-paste-row={r.row} data-paste-row-status={r.status} className={r.status === "excluida" ? "inactive" : ""}>
      <td className="muted tabular">{r.row}</td>
      <td className="tabular">{r.date ? fmtDay(r.date) : <span className="muted">{r.cells[0] ?? "—"}</span>}</td>
      <td>{r.description ?? <span className="muted">{r.cells.join(" · ")}</span>}{r.document && <div className="muted small">Doc. {r.document}</div>}
        {r.errors.map((e) => <div key={e.code} className="small neg" data-paste-error={e.code}>{e.message}</div>)}
        {r.duplicates.map((x, i) => <div key={i} className="small muted" data-paste-dup={x.level}>{x.level === "lote" ? `Igual à linha ${x.ofRow} deste lote.` : `Igual a ${x.lineIds.length === 1 ? "uma linha já gravada" : `${x.lineIds.length} linhas já gravadas`} nesta conta.`}</div>)}</td>
      <td className={`num tabular ${r.amount && r.amount.cents < 0 ? "neg" : "pos"}`}>{r.amount ? `${r.amount.cents < 0 ? "−" : "+"} ${formatMoney(r.amount.decimal.replace("-", ""), d.currency)}` : "—"}</td>
      <td><span className={cls}>{label}</span></td>
      <td><span className="actions">
        {r.status === "duplicada" && <label className="small"><input type="checkbox" checked={confirmed} onChange={onToggle} name={`dup-${r.row}`} data-confirm-dup-row={r.row} /> Gravar mesmo assim</label>}
        {(r.status === "duplicada" || r.status === "erro") && <button className="link danger" onClick={onRemove} data-remove-row={r.row}>Remover do lote</button>}
      </span></td>
    </tr>
  );
}

